import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Chip, Modal } from "../components/ui-bits";
import {
  ArrowLeft, Upload, Trash2, FileText, Image as ImageIcon, Download, Grid,
  List as ListIcon, AlertTriangle, Layers,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useBrandPermissions } from "../lib/brand";
import { SamplingDashboard } from "../components/sampling/SamplingDashboard";

const FILE_KINDS = [
  { key: "collection_image", label: "Image", accept: "image/*" },
  { key: "collection_lookbook", label: "Lookbook", accept: "image/*,application/pdf" },
  { key: "collection_sketch", label: "Sketch", accept: "image/*,application/pdf" },
  { key: "collection_fabric", label: "Fabric reference", accept: "image/*,application/pdf" },
  { key: "collection_techpack", label: "Tech Pack PDF", accept: "application/pdf" },
  { key: "collection_article_sheet", label: "Article sheet", accept: "application/pdf,image/*" },
  { key: "collection_pdf", label: "Other PDF", accept: "application/pdf" },
];

const DEV_STATUSES = ["draft","approved","in_sampling","in_production","completed"];

const isImg = (ct) => (ct || "").startsWith("image/");

export default function CollectionDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { canEdit } = useBrandPermissions();

  const [tab, setTab] = useState("overview");
  const [collection, setCollection] = useState(null);
  const [files, setFiles] = useState([]);
  const [plan, setPlan] = useState(null);
  const [uploadKind, setUploadKind] = useState("collection_image");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [view, setView] = useState("grid");
  const [filter, setFilter] = useState({ category: "", gender: "", dev_status: "" });
  const [error, setError] = useState("");

  const loadColl = async () => {
    // there's no direct GET, so re-list and filter
    const r = await api.get("/collections");
    setCollection(r.data.find(c => c.id === id) || null);
  };
  const loadFiles = async () => {
    const kinds = FILE_KINDS.map(k => k.key);
    const promises = kinds.map(k => api.get("/files", { params: { linked_type: k, linked_id: id } }).then(r=>r.data));
    const all = (await Promise.all(promises)).flat();
    setFiles(all);
  };
  const loadPlan = async () => {
    try {
      const r = await api.get(`/collections/${id}/plan`);
      setPlan(r.data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    loadColl(); loadFiles(); loadPlan();
   
  }, [id]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("linked_type", uploadKind);
      fd.append("linked_id", id);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      loadFiles();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeFile = async (fid) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/files/${fid}`);
    loadFiles();
  };

  const download = (fid, name) => {
    const a = document.createElement("a"); a.href = fileUrl(fid); a.download = name; a.click();
  };

  const downloadPlanPdf = async () => {
    const r = await api.get(`/pdf/collection-plan/${id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `collection-plan-${collection?.name || "doc"}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!collection) return <div className="text-[#737373]">Loading collection...</div>;

  const filtered = (plan?.garments || []).filter(g => {
    if (filter.category && (g.category || "Uncategorized") !== filter.category) return false;
    if (filter.gender && (g.gender || "unassigned") !== filter.gender) return false;
    if (filter.dev_status && (g.dev_status || "draft") !== filter.dev_status) return false;
    return true;
  });

  const missingIds = new Set((plan?.missing_data || []).map(m => m.id));

  const imgUrl = (fid) => fileUrl(fid);

  return (
    <div data-testid="collection-detail-page">
      <button onClick={()=>nav("/collections")} className="text-xs text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-2 mb-6 uppercase tracking-widest">
        <ArrowLeft size={14}/> All collections
      </button>

      <PageHeader
        eyebrow={collection.season ? `${collection.season} ${collection.year || ""}` : "Collection"}
        title={collection.name}
        description={collection.description}
        action={<Chip tone={collection.status === "complete" ? "green" : collection.status === "in_production" ? "gold" : "default"}>{collection.status?.replaceAll("_"," ")}</Chip>}
      />

      <div className="flex gap-1 border-b border-[#E5E5E5] mb-6 overflow-x-auto">
        {[
          { key: "overview", label: "Overview" },
          { key: "sampling", label: "Campionario" },
          { key: "files", label: "Files" },
          { key: "plan", label: "Collection Plan" },
        ].map(t => (
          <button
            key={t.key}
            data-testid={`coll-tab-${t.key}`}
            onClick={()=>setTab(t.key)}
            className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 transition-colors ${tab===t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "sampling" && <SamplingDashboard collectionId={collection.id}/>}
      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card-dark p-6"><div className="label-caps mb-3">Articles</div><div className="font-serif text-4xl">{plan?.total_articles ?? "—"}</div></div>
          <div className="card-dark p-6"><div className="label-caps mb-3">Categories</div><div className="font-serif text-4xl">{plan?.by_category?.length ?? "—"}</div></div>
          <div className="card-dark p-6"><div className="label-caps mb-3">Files uploaded</div><div className="font-serif text-4xl">{files.length}</div></div>
          <div className="card-dark p-6 md:col-span-3">
            <div className="label-caps mb-4">Development status breakdown</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {DEV_STATUSES.map(s => {
                const found = plan?.by_dev_status?.find(x=>x.name===s);
                return (
                  <div key={s} className="border border-[#E5E5E5] p-4">
                    <div className="label-caps mb-2">{s.replaceAll("_"," ")}</div>
                    <div className="font-serif text-2xl">{found?.count ?? 0}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "files" && (
        <>
          {canEdit && (
            <div className="card-dark p-5 mb-6 flex flex-wrap items-center gap-3">
              <div className="label-caps text-[#0A0A0A]">Upload:</div>
              <select data-testid="upload-kind" className="input-dark max-w-xs" value={uploadKind} onChange={(e)=>setUploadKind(e.target.value)}>
                {FILE_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
              <button
                data-testid="upload-btn"
                disabled={uploading}
                onClick={()=>fileRef.current?.click()}
                className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Upload size={14}/> {uploading ? "Uploading..." : "Choose file"}
              </button>
              <input ref={fileRef} type="file" hidden onChange={upload}
                accept={FILE_KINDS.find(k=>k.key===uploadKind)?.accept}/>
              {error && <div className="text-red-600 text-xs">{error}</div>}
            </div>
          )}

          {files.length === 0 ? <Empty title="No files uploaded yet"/> : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="collection-files">
              {files.map(f => (
                <div key={f.id} className="card-dark overflow-hidden group" data-testid={`file-${f.id}`}>
                  <div className="aspect-square bg-[#FAFAFA] flex items-center justify-center">
                    {isImg(f.content_type) ? (
                      <img src={imgUrl(f.id)} alt={f.original_filename} className="w-full h-full object-cover"/>
                    ) : (
                      <FileText size={40} className="text-[#0A0A0A]" strokeWidth={1}/>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="label-caps text-[10px] text-[#0A0A0A] mb-1">
                      {FILE_KINDS.find(k=>k.key===f.linked_type)?.label || f.linked_type}
                    </div>
                    <div className="text-xs truncate" title={f.original_filename}>{f.original_filename}</div>
                    <div className="flex items-center justify-between mt-2">
                      <button onClick={()=>download(f.id, f.original_filename)} className="text-[#525252] hover:text-[#0A0A0A]" title="Download"><Download size={12}/></button>
                      {canEdit && <button onClick={()=>removeFile(f.id)} className="text-[#525252] hover:text-red-600"><Trash2 size={12}/></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "plan" && plan && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="card-dark p-4"><div className="label-caps">Articles</div><div className="font-serif text-3xl">{plan.total_articles}</div></div>
            <div className="card-dark p-4"><div className="label-caps">Categories</div><div className="font-serif text-3xl">{plan.by_category.length}</div></div>
            <div className="card-dark p-4"><div className="label-caps">Genders</div><div className="font-serif text-3xl">{plan.by_gender.length}</div></div>
            <div className="card-dark p-4"><div className="label-caps">Colors</div><div className="font-serif text-3xl">{plan.colors.length}</div></div>
            <div className="card-dark p-4"><div className="label-caps">Sizes</div><div className="font-serif text-3xl">{plan.sizes.length}</div></div>
          </div>

          {plan.missing_data.length > 0 && (
            <div className="card-dark p-5 mb-6 border-l-2 border-l-[#0A0A0A]">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={14} className="text-[#0A0A0A]"/>
                <div className="label-caps text-[#0A0A0A]">{plan.missing_data.length} article(s) missing data</div>
              </div>
              <div className="text-xs text-[#525252] space-y-1 max-h-40 overflow-auto">
                {plan.missing_data.map(m => (
                  <div key={m.id} className="flex items-center justify-between">
                    <span><span className="text-[#0A0A0A]">{m.code}</span> — {m.name}</span>
                    <span className="text-[#737373]">{m.missing.join(", ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select data-testid="plan-filter-category" className="input-dark max-w-xs" value={filter.category} onChange={(e)=>setFilter({...filter, category: e.target.value})}>
              <option value="">All categories</option>
              {plan.by_category.map(c => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </select>
            <select data-testid="plan-filter-gender" className="input-dark max-w-xs" value={filter.gender} onChange={(e)=>setFilter({...filter, gender: e.target.value})}>
              <option value="">All genders</option>
              {plan.by_gender.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <select data-testid="plan-filter-status" className="input-dark max-w-xs" value={filter.dev_status} onChange={(e)=>setFilter({...filter, dev_status: e.target.value})}>
              <option value="">All statuses</option>
              {DEV_STATUSES.map(s => <option key={s} value={s}>{s.replaceAll("_"," ")}</option>)}
            </select>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={()=>setView("grid")} className={`p-2 border ${view==="grid"?"border-[#0A0A0A] text-[#0A0A0A]":"border-[#E5E5E5] text-[#525252]"}`}><Grid size={14}/></button>
              <button onClick={()=>setView("table")} className={`p-2 border ${view==="table"?"border-[#0A0A0A] text-[#0A0A0A]":"border-[#E5E5E5] text-[#525252]"}`}><ListIcon size={14}/></button>
              <button data-testid="plan-export-pdf" onClick={downloadPlanPdf} className="btn-ghost inline-flex items-center gap-2"><Download size={14}/> Export PDF</button>
            </div>
          </div>

          {filtered.length === 0 ? <Empty title="No articles match the filters"/> : view === "grid" ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {filtered.map(g => (
                <Link key={g.id} to={`/garments`} onClick={()=>{}} className="card-dark overflow-hidden hover-lift">
                  <div className="aspect-[4/5] bg-[#FAFAFA] relative">
                    {g.main_image_id ? (
                      <img src={imgUrl(g.main_image_id)} className="w-full h-full object-cover"/>
                    ) : g.images?.[0] ? (
                      <img src={imgUrl(g.images[0])} className="w-full h-full object-cover"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#D4D4D4]"><ImageIcon size={32}/></div>
                    )}
                    {missingIds.has(g.id) && (
                      <div className="absolute top-2 right-2"><Chip tone="red">Incomplete</Chip></div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="label-caps text-[#0A0A0A]">{g.code}</div>
                    <div className="font-serif text-lg leading-tight">{g.name}</div>
                    <div className="text-xs text-[#525252] mt-1">{g.category || "—"} · {g.gender || "—"}</div>
                    <div className="mt-2"><Chip tone={g.dev_status === "completed" ? "green" : g.dev_status === "in_production" ? "gold" : "default"}>{(g.dev_status || "draft").replaceAll("_"," ")}</Chip></div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="card-dark">
              <table className="tbl">
                <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Gender</th><th>Status</th><th>Missing</th></tr></thead>
                <tbody>
                  {filtered.map(g => (
                    <tr key={g.id}>
                      <td className="text-[#0A0A0A]">{g.code}</td>
                      <td>{g.name}</td>
                      <td>{g.category || "—"}</td>
                      <td>{g.gender || "—"}</td>
                      <td><Chip tone={g.dev_status === "completed" ? "green" : g.dev_status === "in_production" ? "gold" : "default"}>{(g.dev_status||"draft").replaceAll("_"," ")}</Chip></td>
                      <td className="text-[#737373] text-xs">{(plan.missing_data.find(m=>m.id===g.id)?.missing || []).join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
